/**
 * El paso del asistente en la URL (`?paso=<nombre>`). Los pasos van POR NOMBRE (misma lección que
 * `CampanaNuevaRealPage.tsx`: con índices, agregar un paso corría todos y un `current === 4` mostraba otro paso).
 *
 * - El paso de la URL se respeta sólo si es alcanzable con lo que la campaña ya tiene guardado (`maxAlcanzado`, que
 *   fija quien carga el borrador); si no, se muestra el más lejano que sí. Quien carga también corrige la URL, con
 *   `irA(…, true)`: sin sumar una entrada al historial.
 * - Los nombres de los seis pasos de antes (29/10/2026) siguen valiendo (`ALIAS_PASOS`): `?paso=plantilla` muestra
 *   «Qué les decís» y la URL se corrige al nombre nuevo con replace, sin tocar el resto (`?plantilla=`, `?publico=`).
 * - `alIr`: lo que se limpia al cambiar de paso (el error y el aviso del paso).
 */

import { useCallback, useEffect, useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import { PASOS, type PasoAsistente } from "../../logic/chequeo";
import { idx, pasoDeLaUrl } from "../../logic/asistente";

export function usePasoEnUrl({ sp, setSp, alIr }: { sp: URLSearchParams; setSp: SetURLSearchParams; alIr: () => void }) {
  const [maxAlcanzado, setMaxAlcanzado] = useState(0);

  const crudo = sp.get("paso");
  const pedido = pasoDeLaUrl(crudo);
  const paso: PasoAsistente = pedido && idx(pedido) <= maxAlcanzado ? pedido : PASOS[Math.min(maxAlcanzado, PASOS.length - 1)];

  // Un link con un nombre viejo: la URL pasa al nuevo, así un F5 o un link copiado ya lo traen.
  useEffect(() => {
    if (!crudo || !pedido || crudo === pedido) return;
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      n.set("paso", pedido);
      return n;
    }, { replace: true });
  }, [crudo, pedido, setSp]);

  const irA = useCallback((p: PasoAsistente, reemplazar = false, sinPublico = false) => {
    alIr();
    setMaxAlcanzado((m) => Math.max(m, idx(p)));
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      n.set("paso", p);
      n.delete("plantilla");
      // Con la audiencia guardada, `?publico=` ya cumplió: si quedara, la pestaña de la base mandaría sobre lo guardado.
      if (sinPublico) n.delete("publico");
      return n;
    }, { replace: reemplazar });
  }, [setSp, alIr]);

  return { paso, maxAlcanzado, setMaxAlcanzado, irA };
}
